import React, { useCallback } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Spinner,
  Container
} from "@/Components/Common/Tailwind";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../../../api";
import moment from "moment";
import { useInfiniteQuery } from "@tanstack/react-query";
import Loader from "../../../Components/Common/Loader";
import Navbar from "../../../Components/Common/navbar";
import { getAccessToken } from "../../../api/auth";
import AmazingPassInfoModal from "../../../Components/modal/AmazingPassInfoModal";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import { ChevronLeft, ChevronRight, ArrowRight, Info, Hexagon } from "lucide-react";
import { cn } from "@/Components/ui/utils";

function AmazingPassPurchaseList(props) {
  const navigate = useNavigate();

  const [modalBackdrop, setModalBackdrop] = React.useState(false);
  function toggleBackdrop() {
    setModalBackdrop(!modalBackdrop);
  }

  const [couponModal, setCouponModal] = React.useState(false);
  const [selectCoupon, setSelectCoupon] = React.useState({});

  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const accessToken = getAccessToken();

  const fetchData = useCallback(
    async ({ pageParam = 1 }) => {
      const response = await API.get(`/precoupon/user/?page=${pageParam}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      const data = response.data;

      return { ...data, results: data.results };
    },
    [accessToken]
  );

  const { status, data, error, isSuccess, isFetching, isLoading, isFetchingNextPage, isFetchingPreviousPage, fetchNextPage, fetchPreviousPage, hasNextPage, hasPreviousPage, refetch } =
    useInfiniteQuery({
      queryKey: ["AmazingPassList"],
      queryFn: async ({ pageParam = 1 }) => {
        const data = await fetchData({ pageParam });
        return data;
      },
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.next) {
          const match = lastPage.next.match(/page=(\d+)/);
          if (match) {
            return match[1];
          }
        }
        return undefined;
      },
    });

  // 사용가능(state === "사용안함")을 위로, 사용완료를 아래로 정렬.
  // Array.sort는 stable이라 같은 상태 내 서버 정렬(만료 임박 순 등)은 유지된다.
  const sortedItems = React.useMemo(() => {
    const items = data?.pages.flatMap((page) => page.results) ?? [];
    return [...items].sort((a, b) => {
      const aDone = a.state === "사용안함" ? 0 : 1;
      const bDone = b.state === "사용안함" ? 0 : 1;
      return aDone - bDone;
    });
  }, [data]);

  return (
    <React.Fragment>
      <MobileViewContainer>
        {/* Fixed Header */}
        <header className="fixed top-0 left-1/2 -translate-x-1/2 z-50 w-full max-w-160 flex items-center justify-center h-14 px-2 bg-white/80 backdrop-blur-md border-b border-gray-100">
          <Link
            to="/my-page"
            aria-label="뒤로"
            className="absolute left-2 inline-flex items-center justify-center w-10 h-10 rounded-full text-black no-underline hover:bg-gray-100 transition-colors active:scale-[0.96]"
          >
            <ChevronLeft size={22} />
          </Link>
          <div className="flex items-center">
            <div
              className={cn(
                "overflow-hidden transition-[max-width,opacity,margin] duration-300",
                scrolled ? "max-w-0 opacity-0 mr-0" : "max-w-[40px] opacity-100 mr-1.5"
              )}
            >
              <div className="bg-[#f6475f]/10 p-1.5 rounded-lg shrink-0">
                <Hexagon size={16} className="text-[#f6475f]" />
              </div>
            </div>
            <h1 className="text-lg font-bold text-black" style={{ fontFamily: "Gmarket-B" }}>어메이징패스</h1>
          </div>
        </header>

        <Container className="px-4 pt-[72px]" style={{ minHeight: "100vh", backgroundColor: "#f9fafb", paddingBottom: "100px" }}>
          <div className="flex flex-col gap-6">
            {/* Reservation Prompt */}
            <div className="space-y-3">
              <h1 className="text-xl font-bold text-black" style={{ fontFamily: "Gmarket-B" }}>패스로 예약하기</h1>
              <div className="bg-white rounded-2xl p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.06)]">
                <p className="text-[15px] leading-relaxed text-gray-700 mb-4" style={{ fontFamily: "Gmarket-M" }}>보유하신 어메이징패스로<br />원하는 상품을 예약해 보세요!</p>
                <Button
                  className="w-full py-4 font-bold rounded-xl border-0 text-white bg-[#f6475f] hover:bg-[#e63950] shadow-[0_8px_20px_rgba(246,71,95,0.35)] transition active:scale-[0.96]"
                  style={{ fontFamily: "Gmarket-M" }}
                  onClick={() => navigate("/amazingpass")}
                >
                  예약하기
                </Button>
              </div>
            </div>

            {/* List Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-gray-700" style={{ fontFamily: "Gmarket-M" }}>
                보유 내역 <span className="text-gray-700 tabular-nums">({data?.pages?.[0]?.count ?? 0})</span>
              </h3>

              {isLoading && (
                <div className="flex justify-center py-10">
                  <Spinner />
                </div>
              )}

              {data && isSuccess ? (
                <div className="bg-white rounded-2xl overflow-hidden divide-y divide-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.05)]">
                  {sortedItems.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        "flex items-center justify-between px-4 py-3.5 cursor-pointer transition-colors hover:bg-[#f6475f]/5 active:bg-[#f6475f]/10",
                        item.state !== "사용안함" && "opacity-50 grayscale"
                      )}
                      onClick={() => {
                        setSelectCoupon(item);
                        setCouponModal(true);
                      }}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0 outline outline-1 -outline-offset-1 outline-black/10">
                          <img src={item.image} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-[15px] font-bold text-black truncate" style={{ fontFamily: "Gmarket-B" }}>
                            {item.precoupon_name}
                          </span>
                          <span className="text-xs text-gray-700 mt-0.5">
                            {item.state === "사용안함" ? "사용가능" : item.state} • ~{moment(item.expire_date).format("YY.MM.DD")}
                          </span>
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-gray-500 flex-shrink-0 ml-2" />
                    </div>
                  ))}
                </div>
              ) : null}

              {hasNextPage && (
                <button
                  className="w-full py-4 text-sm font-bold text-[#f6475f] bg-white border border-[#f6475f]/30 rounded-xl mt-4 transition active:scale-[0.96] hover:bg-[#f6475f]/5"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                  style={{ fontFamily: "Gmarket-M" }}
                >
                  {isFetchingNextPage ? "로딩중..." : "어메이징패스 더보기"}
                </button>
              )}

              {data && data?.pages[0].count === 0 && !isLoading && (
                <div className="text-center py-20 text-gray-700 text-pretty break-keep">
                  보유하신 어메이징패스가 없습니다.
                </div>
              )}
            </div>
          </div>

          <AmazingPassInfoModal setVisible={setCouponModal} visible={couponModal} info={selectCoupon} setInfo={setSelectCoupon} refetch={refetch} />
        </Container>
      </MobileViewContainer>
    </React.Fragment>
  );
}

export default AmazingPassPurchaseList;
